/**
 * SatelliteUsage — admin dashboard for the satellite platform: per-tool cost/usage,
 * budget status, and recent build failures. Reads the usage ledger + requests.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

const money = (n) => '$' + (Number(n) || 0).toFixed(4);

export default function SatelliteUsage() {
  const [stats, setStats] = useState(null);
  const [fails, setFails] = useState([]);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/satellite-usage/stats?window_hours=720`),
        authFetch(`${BACKEND_URL}/Api/satellite-requests/all`),
      ]);
      if (!s.ok) throw new Error((await s.json()).error || s.status);
      setStats(await s.json());
      if (r.ok) setFails((await r.json()).filter((x) => x.status === 'failed'));
      setErr(null);
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const tools = stats?.tools || [];
  const overBudget = tools.filter((t) => t.over_budget);
  const card = { border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', minWidth: 130 };
  const num = { fontSize: 22, fontWeight: 700 };
  const th = { textAlign: 'left', fontSize: 11, color: '#64748b', textTransform: 'uppercase', padding: '6px 10px', borderBottom: '1px solid #e2e8f0' };
  const td = { fontSize: 13, padding: '8px 10px', borderBottom: '1px solid #f1f5f9' };

  return (
    <div style={{ maxWidth: 820 }}>
      <h3 style={{ margin: '0 0 4px' }}>Satelliet-gebruik</h3>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0 }}>
        Kosten, tokengebruik en budgetstatus per tool (laatste 30 dagen). Ververst elke 15s.
      </p>
      {err && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {stats && (
        <div style={{ display: 'flex', gap: 12, margin: '12px 0' }}>
          <div style={card}><div style={{ fontSize: 11, color: '#64748b' }}>CALLS</div><div style={num}>{stats.totals.calls}</div></div>
          <div style={card}><div style={{ fontSize: 11, color: '#64748b' }}>TOKENS</div><div style={num}>{stats.totals.tokens.toLocaleString()}</div></div>
          <div style={card}><div style={{ fontSize: 11, color: '#64748b' }}>KOSTEN</div><div style={num}>{money(stats.totals.cost_usd)}</div></div>
        </div>
      )}

      {(overBudget.length > 0 || fails.length > 0) && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', margin: '10px 0' }}>
          <b style={{ fontSize: 13, color: '#b91c1c' }}>Aandacht</b>
          {overBudget.map((t) => (
            <div key={t.tool} style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 4 }}>
              💸 <b>{t.tool}</b> over budget: {money(t.cost_month_usd)} / {money(t.budget_usd_month)} deze maand
            </div>
          ))}
          {fails.slice(0, 5).map((f) => (
            <div key={f.id} style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 4 }}>
              ⚠️ build mislukt: <b>{f.tool_slug}</b>{f.error_detail ? ` — ${f.error_detail}` : ''}
            </div>
          ))}
        </div>
      )}

      <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
        <thead><tr>
          <th style={th}>Tool</th><th style={th}>Calls</th><th style={th}>Tokens</th>
          <th style={th}>Kosten (30d)</th><th style={th}>Deze maand</th><th style={th}>Budget</th>
        </tr></thead>
        <tbody>
          {tools.length === 0 && <tr><td style={td} colSpan={6}>Nog geen gebruik.</td></tr>}
          {tools.map((t) => (
            <tr key={t.tool} style={t.over_budget ? { background: '#fef2f2' } : {}}>
              <td style={td}><b>{t.tool}</b></td>
              <td style={td}>{t.calls}</td>
              <td style={td}>{t.tokens.toLocaleString()}</td>
              <td style={td}>{money(t.cost_usd)}</td>
              <td style={td}>{money(t.cost_month_usd)}</td>
              <td style={td}>{t.budget_usd_month != null ? money(t.budget_usd_month) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
